import type { DocumentPageSchema } from '@avisoma/shared';
import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import backend from '../../services/backend';
import { PagePreviewHoverProvider } from '../../components/pagePreview/usePagePreviewHover';
import { SegmentChildThumb } from './SegmentChildThumb';

interface SegmentPreviewsProps {
  page: DocumentPageSchema;
  // Hovering a segment thumbnail reports that child's bounds so the parent
  // preview can highlight where the section was cropped from (null on leave).
  onHoverSection?: (bounds: DocumentPageSchema['sectionBounds']) => void;
}

// Thumbnails of each CHILD segment-page (in section order) for a multi-section
// PARENT page, rendered inline inside the Segmentation stage output. Shell-less:
// the surrounding card/heading belong to the caller. Renders nothing for a
// non-segmented page.
export function SegmentPreviews({ page, onHoverSection }: SegmentPreviewsProps) {
  const segmentation = page.processing?.segmentation;
  const segmentPageIds =
    segmentation?.status === 'segmented' ? (segmentation.result?.segmentPageIds ?? []) : [];

  // No batch/children endpoint exists, so fetch each child by id in parallel via
  // the same single-page query the rest of the view uses. Hooks must run
  // unconditionally, so this is computed before the early return below.
  const childQueries = useQueries({
    queries: segmentPageIds.map((childId) => ({
      queryKey: ['documentPage', childId],
      queryFn: () => backend.getDocumentPage(childId).then((res) => res.data),
      enabled: segmentPageIds.length > 0,
      staleTime: 30_000,
      // Freshly-forked children are still running pdfParser/classification/etc.;
      // poll until each child's stages settle so its heading, classification and
      // status dots fill in live (the parent page query stops polling once it's
      // `segmented`, so without this the thumbnails would stay stale until refresh).
      refetchInterval: (q: { state: { data?: DocumentPageSchema } }) => {
        const p = q.state.data?.processing;
        if (!p) return 2500;
        const settled = [
          p.pdfParser?.status,
          p.metadata?.status,
          p.embeddings?.status,
          p.classification?.status,
          p.dataExtraction?.status,
        ].every((s) => s && ['done', 'error', 'skip'].includes(s));
        return settled ? false : 2500;
      },
    })),
  });

  // Highlight the hovered section's band on the parent preview. Track the hovered
  // child ID (not the bounds captured at mouse-enter) and resolve its bounds
  // reactively, so a thumbnail hovered BEFORE its page query resolves still lights
  // up the moment the bounds arrive — and clears on leave / when unavailable.
  // Declared before the early return so the hook order stays stable.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hoveredIdx = hoveredId ? segmentPageIds.indexOf(hoveredId) : -1;
  const hoveredBounds = hoveredIdx >= 0 ? (childQueries[hoveredIdx]?.data?.sectionBounds ?? null) : null;
  useEffect(() => {
    onHoverSection?.(hoveredBounds);
  }, [hoveredBounds, onHoverSection]);

  // Clear the parent highlight on unmount: leaving the view while a segment is
  // hovered would otherwise strand the parent preview band lit until the next
  // hover update (`onHoverSection` is a stable setState setter, so this cleanup
  // runs on unmount only).
  useEffect(() => {
    return () => onHoverSection?.(null);
  }, [onHoverSection]);

  if (segmentPageIds.length === 0) return null;

  // Sort by the child's section order (falling back to the id list order) so the
  // previews read top-to-bottom like the original page.
  const children = segmentPageIds
    .map((childId, index) => ({ childId, query: childQueries[index] }))
    .sort((a, b) => {
      const ao = a.query.data?.sectionBounds?.order;
      const bo = b.query.data?.sectionBounds?.order;
      if (typeof ao === 'number' && typeof bo === 'number') return ao - bo;
      return 0;
    });

  // Ordered sibling ids so the full-screen preview can flip between sections.
  const orderedIds = children.map((c) => c.childId);

  return (
    <PagePreviewHoverProvider visibleIds={new Set(orderedIds)}>
      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {children.map(({ childId, query }) => (
          <SegmentChildThumb
            key={childId}
            childId={childId}
            child={query.data}
            loading={query.isLoading}
            error={query.isError}
            siblingIds={orderedIds}
            onHover={() => setHoveredId(childId)}
            onLeave={() => setHoveredId((cur) => (cur === childId ? null : cur))}
          />
        ))}
      </div>
    </PagePreviewHoverProvider>
  );
}
